import {
  Factory,
  type ID,
  isLibraryRef,
  LibraryActions,
  type LibrarySymbol,
  ProjectActions,
  pickColor,
  type SymbolItem,
  selectLibraryItem,
} from '@octopus-do/octopus-project-core'
import {
  LibraryNotifications,
  LibrarySagaActions,
  selectOwnLibrarySymbolIds,
} from '@store/librarySaga'
import type { AppThunk } from '@store/migrationActions/thunkActions/types'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { getId } from '@store/utils'
import { commitProjectRequest } from '@storeOld/actionCreators'
import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'

import { createLibraryItem } from '@/services/library'
import { OpenConfirmation } from '@/store'

export {
  thunkEnsureLibraryColors,
  thunkUpdateLibrarySymbol,
  thunkUpdateLibrarySymbolColor,
  thunkUpdateSymbolColor,
  thunkUpdateSymbolContent,
  thunkUpdateSymbolTitle,
  thunkUpdateSymbolVisual,
  thunkUpdateSymbolWireframes,
  thunkUploadLibrarySymbolImage,
  thunkUploadSymbolImage,
} from './thunkSymbolEdit'

const DeleteSymbolMessage =
  'Deleting this symbol will remove all linked blocks in the sitemap.'

const RemoveFromLibraryMessage =
  'The symbol will be removed from every project of this workspace that uses it.'

export const thunkAddSymbol = (): AppThunk => (dispatch, getState) => {
  const id = getId()
  const colorId = migrationProjectSelector.getProjectDefaultColorId(getState())

  const symbol: SymbolItem = Factory.symbol({
    id,
    data: Factory.block.extractableData({
      title: '',
      colorId,
    }),
  })

  dispatch({
    type: 'COMMIT_PROJECT_REQUEST',
    payload: [
      ProjectActions.symbols.add({
        items: [symbol],
      }),
    ],
  })

  dispatch({
    type: CanvasActionTypes.SELECT_TO_EDIT_SYMBOL,
    payload: id,
  })
}

const thunkUnlinkAndDeleteSymbol =
  ({ id }: { id: string }): AppThunk =>
  (dispatch, getState) => {
    const dependents = migrationProjectSelector.getProjectSymbolDependents(id)(
      getState()
    )

    const showConfirm = dependents.length > 0

    const actualAction = commitProjectRequest(
      ProjectActions.blocks.delete({ ids: dependents }),
      ProjectActions.symbols.delete({ id })
    )

    if (!showConfirm) {
      dispatch(actualAction)
      return
    }

    dispatch(
      OpenConfirmation({
        label: 'Delete',
        message: DeleteSymbolMessage,
        title: 'Delete symbol',
        then: [actualAction],
      })
    )
  }

export const thunkCheckUsageAndDeleteSymbol =
  ({ id }: { id: string }): AppThunk =>
  (dispatch) => {
    dispatch(thunkUnlinkAndDeleteSymbol({ id }))

    dispatch({
      type: CanvasActionTypes.CLEAR_SELECTED_SYMBOL,
    })
  }

/** A ref becomes a project symbol with the data it renders with now. */
export const thunkDetachSymbol =
  ({ id }: { id: ID }): AppThunk =>
  (dispatch, getState) => {
    const state = getState()
    const symbol = migrationProjectSelector.getProjectSymbolById(id)(state)

    if (!symbol || !isLibraryRef(symbol)) {
      return
    }

    const data = migrationProjectSelector.getProjectSymbolData(id)(state)

    dispatch(commitProjectRequest(ProjectActions.symbols.detach({ id, data })))
  }

/**
 * A new symbol of the workspace library, created right in the library tab the
 * way a project symbol is: no name, the default color of the project. Returns
 * the id of the new item, so the caller can put the name in at once.
 */
export const thunkAddLibrarySymbol =
  (): AppThunk<Promise<ID | null>> => async (dispatch, getState) => {
    const state = getState()
    const { colors } = state.manager.project
    const defaultColorId =
      migrationProjectSelector.getProjectDefaultColorId(state)
    const color = colors.find((c) => c.id === defaultColorId)
    const libraryColor = color
      ? { hexCode: color.hexCode, title: color.title }
      : null
    const workspaceId = migrationProjectSelector.getLibraryWorkspaceParam(state)

    try {
      const item = await createLibraryItem(workspaceId, 'symbol', {
        color: libraryColor,
        content: null,
        title: '',
        visual: { type: 'wireframes', wireframes: [] },
      })
      const own = selectOwnLibrarySymbolIds(getState())

      dispatch(
        LibraryActions.upsert({
          kind: 'symbol',
          items: [item as LibrarySymbol],
        })
      )
      dispatch(
        LibraryActions.setOwn({ kind: 'symbol', ids: [...own, item.id] })
      )

      return item.id
    } catch (error) {
      console.error('Failed to create the library symbol', error)

      dispatch(LibraryNotifications.failed('Failed to create the symbol'))

      return null
    }
  }

export const thunkRemoveSymbolFromLibrary =
  ({ libraryId }: { libraryId: ID }): AppThunk =>
  (dispatch) => {
    dispatch(
      OpenConfirmation({
        label: 'Remove',
        message: RemoveFromLibraryMessage,
        title: 'Remove from library',
        then: [LibrarySagaActions.removeSymbol({ libraryId })],
      })
    )
  }

/**
 * Brings a library symbol into the project as a ref and returns its id. A
 * project already referencing the item keeps its ref. The library color is a
 * default: it maps onto the palette by hex, or joins it.
 */
export const thunkPullLibrarySymbol =
  (libraryId: ID): AppThunk<ID | null> =>
  (dispatch, getState) => {
    const state = getState()

    const existing = state.manager.project.symbols.find(
      (symbol) => isLibraryRef(symbol) && symbol.libraryId === libraryId
    )

    if (existing) {
      return existing.id
    }

    const item = selectLibraryItem('symbol', libraryId)(state)

    if (!item) {
      return null
    }

    const picked = pickColor(
      item.data.color,
      state.manager.project.colors,
      getId()
    )
    const colorId =
      picked.colorId ?? migrationProjectSelector.getProjectDefaultColorId(state)

    const id = getId()
    const ref = Factory.symbolRef({ id, libraryId, colorId })

    const colorActions = picked.colorToAdd
      ? [ProjectActions.colors.add({ items: [picked.colorToAdd] })]
      : []

    dispatch(
      commitProjectRequest(
        ...colorActions,
        ProjectActions.symbols.add({ items: [ref] })
      )
    )

    return id
  }
